import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IEmployeeRetrieved, IUserWithQueue } from "@/Settings/types/QueueMapping/QueueMapping";

export async function getUnEnrolledEmployeesByLocation(locationId: string) {
	const query = gql`
		query getUnenrrolledEmployees($locationId: String!, $position: String) {
			getUnenrrolledEmployees(locationId: $locationId, position: $position) {
				_id
				employeeId
				fullName
				jobTitle
				payrollSourceId {
					idField
					idValue
					displayId
					companyId
					providerName
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getUnenrrolledEmployees", IEmployeeRetrieved[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getUnenrrolledEmployees;
	} catch (err) {
		errorLogger(err as Error, {
			function: "getUnEnrolledEmployeesByLocation",
			message: "Error retrieving employees by location.",
			variables,
		});
		return null;
	}
}

export async function getQueueLabels(locationId: string, commissionCycleId: string) {
	const query = gql`
		query getQueueLabels($locationId: String!, $commissionCycleId: String!) {
			getQueueLabels(locationId: $locationId, commissionCycleId: $commissionCycleId)
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
	};

	try {
		const response: GraphqlQueryResponse<"getQueueLabels", string[]> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getQueueLabels;
	} catch (err) {
		errorLogger(err as Error, { function: "getQueueLabels", message: "Failed to get queue labels." });
		return null;
	}
}

export async function getQueues(locationId: string) {
	const query = gql`
		query getQueues($locationId: String!) {
			getQueues(locationId: $locationId) {
				_id
				name
				locationId
				overflowQueues
				usersByPosition {
					_id
					name
					users {
						_id
						name
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getQueues;
	} catch (err) {
		errorLogger(err as Error, { function: "getQueues", message: "Failed to get queues." });
		return null;
	}
}

export async function getUsersWithQueueStatus(locationId: string, managedPositions: string[]) {
	const query = gql`
		query getUsersWithQueueStatus($locationId: String!, $managedPositions: [String]!) {
			getUsersWithQueueStatus(locationId: $locationId, managedPositions: $managedPositions) {
				_id
				fullName
				employeeId
				position
				hasQueue
			}
		}
	`;

	const variables = {
		locationId,
		managedPositions,
	};

	try {
		const response: GraphqlQueryResponse<"getUsersWithQueueStatus", IUserWithQueue[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getUsersWithQueueStatus;
	} catch (err) {
		errorLogger(err as Error, { function: "getUsersWithQueueStatus", message: "Failed to get users with queue status." });
		return null;
	}
}
